import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatCents, type Order, type OrderPrepayment } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';

const tone: Record<OrderPrepayment['state'], string> = { verified: 'alert--ok', pending: 'alert--warn', unavailable: 'alert--warn', failed: 'alert--error', duplicate: 'alert--error' };
const title: Record<OrderPrepayment['state'], string> = {
  verified: 'Vorab per PayPal bezahlt',
  pending: 'PayPal-Zahlung noch nicht abgeschlossen',
  unavailable: 'PayPal-Zahlung noch nicht geprüft',
  failed: 'PayPal-Zahlung nicht bestätigt',
  duplicate: 'PayPal-Zahlung schon vergeben',
};

/**
 * Vorauszahlung per PayPal (KON-250): Die Anfrage nennt eine Zahlung, Wirkia prüft sie beim PayPal-Konto des Mandanten. Hier steht das Ergebnis. Bestätigte Zahlungen verbucht Wirkia nach der
 * Fahrt auf der Rechnung. Bei einer Stornierung oder einer zu hohen Zahlung bleibt eine Erstattung offen, die der Sachbearbeiter in PayPal auslöst und hier abhakt.
 */
export function PrepaymentBanner({ order }: { order: Order }) {
  const { can } = useSession();
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const p = order.prepayment;
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['order', order.id] });
    void client.invalidateQueries({ queryKey: ['orders'] });
  };
  const check = useMutation({
    mutationFn: () => api('POST', `/orders/${order.id}/prepayment/verify`),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Die Prüfung hat nicht geklappt.'),
  });
  const refunded = useMutation({
    mutationFn: () => api('POST', `/orders/${order.id}/prepayment/refunded`),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Das hat nicht geklappt.'),
  });
  if (!p) return null;
  const write = can('orders.write');
  const amount = p.amountCents ?? p.claimedCents;
  // Erstattung offen: Der Auftrag ist storniert und die Zahlung nicht verbucht, oder die Rechnung war niedriger als die Zahlung
  const refundDue = p.state === 'verified' && !p.refundedAt ? (order.status === 'cancelled' && !p.bookedAt ? (p.amountCents ?? 0) : p.refundDueCents) : 0;

  return (
    <div className={`alert ${tone[p.state]}`} role="status">
      <strong>{title[p.state]}{amount ? `: ${formatCents(amount)}` : ''}</strong>
      <div>{p.message}</div>
      {p.bookedAt && <div>Auf Rechnung {p.bookedInvoiceNumber ?? ''} verbucht (Referenz {p.reference}).</div>}
      {p.state === 'verified' && !p.bookedAt && order.status !== 'cancelled' && <div>Wirkia verbucht die Zahlung auf der Rechnung, sobald sie nach der Fahrt erstellt wird.</div>}
      {refundDue > 0 && <div><strong>Erstattung offen: {formatCents(refundDue)}.</strong> Lösen Sie die Erstattung in PayPal aus (Referenz {p.reference}) und haken Sie sie hier ab.</div>}
      {p.refundedAt && <div>Erstattung in PayPal ausgelöst.</div>}
      {error && <div role="alert">{error}</div>}
      {write && (
        <div className="row-actions">
          {p.state !== 'verified' && <button type="button" className="btn btn--secondary btn--sm" onClick={() => check.mutate()} disabled={check.isPending}>{check.isPending ? 'Wird geprüft …' : 'Erneut prüfen'}</button>}
          {refundDue > 0 && <button type="button" className="btn btn--secondary btn--sm" onClick={() => refunded.mutate()} disabled={refunded.isPending}>Erstattung erledigt</button>}
        </div>
      )}
    </div>
  );
}
